/**
 * The board's filter row (point 20, redesigned 2026-09-03).
 *
 * ---- Collapsed by default, and why that is safe here ----
 *
 * The first version laid seven controls across the top of the board, permanently. On a screen
 * whose whole job is showing cards, that is a lot of chrome for something used occasionally — and
 * it pushed the first Kanban column below the fold.
 *
 * A hidden filter is normally a trap: someone leaves one applied, comes back, sees four cards and
 * concludes the board is broken. So collapsing is only safe if what is APPLIED stays visible when
 * the panel is shut. It does — every active filter shows as a chip on the collapsed bar, each
 * removable on its own, with the match count beside it. Shut, the bar is one line and still tells
 * the whole truth; open, it is a proper form.
 *
 * It also opens itself whenever a filter is already set on arrival — the inbox strip's tiles link
 * in with `?status=`, and nobody should have to discover why the board looks narrowed.
 */

import { useState } from 'react'
import { Button } from '../../components/ui'
import { activeFilterCount, EMPTY_FILTERS, type RequirementFilters } from '../../lib/requirementFilters'

/** One applied filter, as the collapsed bar shows it: what it says, and how to drop it. */
function Chip({ label, value, onClear }: { label: string; value: string; onClear: () => void }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 6px 3px 9px',
        borderRadius: 999,
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        fontSize: 12,
        maxWidth: 260,
      }}
    >
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</b>
      <button
        type="button"
        className="linkish"
        aria-label={`Clear ${label} filter`}
        onClick={onClear}
        style={{ fontSize: 14, lineHeight: 1, padding: '0 2px' }}
      >
        ×
      </button>
    </span>
  )
}

export function RequirementFilterBar({
  filters,
  options,
  showing,
  total,
  onChange,
}: {
  filters: RequirementFilters
  options: { customers: { value: string; label: string }[]; enquiryTypes: string[]; purchasers: string[] }
  /** Cards after filtering, and the board's full loaded count — stated whenever they differ. */
  showing: number
  total: number
  onChange: (next: RequirementFilters) => void
}) {
  const active = activeFilterCount(filters)
  // Open on arrival only when something is ALREADY filtering — see the header. Read once as the
  // initial value; after that the control belongs to the user.
  const [open, setOpen] = useState(() => active > 0)
  const set = (patch: Partial<RequirementFilters>) => onChange({ ...filters, ...patch })

  const customerLabel = options.customers.find((c) => c.value === filters.customer)?.label ?? filters.customer
  const dateLabel =
    filters.date_from && filters.date_to
      ? `${filters.date_from} → ${filters.date_to}`
      : filters.date_from
        ? `from ${filters.date_from}`
        : `to ${filters.date_to}`

  return (
    <div className="card" style={{ padding: 0, marginBottom: 12, overflow: 'hidden' }}>
      {/* ---- the bar itself: always one line, always the truth ---- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          padding: '9px 12px',
          borderBottom: open ? '1px solid var(--line)' : 'none',
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid var(--line)',
            background: active > 0 ? 'var(--brand)' : 'var(--panel)',
            color: active > 0 ? '#fff' : 'var(--ink)',
            borderColor: active > 0 ? 'var(--brand)' : 'var(--line)',
            borderRadius: 8,
            padding: '5px 11px',
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          ⚙ Filters
          {active > 0 ? (
            <span
              style={{
                background: 'rgba(255,255,255,.25)',
                borderRadius: 999,
                padding: '0 6px',
                fontSize: 11,
              }}
            >
              {active}
            </span>
          ) : null}
          <span style={{ opacity: 0.7 }}>{open ? '▴' : '▾'}</span>
        </button>

        {/*
          What is applied, even when the panel is shut. This is the whole reason collapsing is not
          a trap: a narrowed board always says why, and each chip drops its own filter without
          reopening the form.
        */}
        {filters.search.trim() ? (
          <Chip label="matching" value={`“${filters.search.trim()}”`} onClear={() => set({ search: '' })} />
        ) : null}
        {filters.customer ? <Chip label="customer" value={customerLabel} onClear={() => set({ customer: '' })} /> : null}
        {filters.enquiry_type ? (
          <Chip label="type" value={filters.enquiry_type} onClear={() => set({ enquiry_type: '' })} />
        ) : null}
        {filters.purchaser ? (
          <Chip label="purchaser" value={filters.purchaser} onClear={() => set({ purchaser: '' })} />
        ) : null}
        {filters.status.length > 0 ? (
          <Chip label="stage" value={filters.status.join(', ')} onClear={() => set({ status: [] })} />
        ) : null}
        {filters.date_from || filters.date_to ? (
          <Chip label="dated" value={dateLabel} onClear={() => set({ date_from: '', date_to: '' })} />
        ) : null}

        <span style={{ flex: 1 }} />

        {active > 0 ? (
          <>
            {/* A filter matching nothing renders an empty board; without this it reads as broken
                rather than filtered. */}
            <span style={{ color: showing === 0 ? 'var(--warn)' : 'var(--muted)', fontSize: 12.5 }}>
              {showing === 0 ? 'No requirement matches' : `${showing} of ${total}`}
            </span>
            <Button size="sm" onClick={() => onChange(EMPTY_FILTERS)}>
              Clear all
            </Button>
          </>
        ) : (
          <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>{total} requirements</span>
        )}
      </div>

      {/* ---- the form, on a grid so the controls line up instead of wrapping raggedly ---- */}
      {open ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 10,
            padding: '12px',
            background: 'var(--bg)',
          }}
        >
          <label className="fld" style={{ gridColumn: 'span 2', minWidth: 0 }}>
            <span>Search</span>
            <input
              type="search"
              value={filters.search}
              onChange={(e) => set({ search: e.target.value })}
              placeholder="Enquiry no, quotation no, customer or vessel…"
            />
          </label>

          <label className="fld" style={{ minWidth: 0 }}>
            <span>Customer</span>
            <select value={filters.customer} onChange={(e) => set({ customer: e.target.value })}>
              <option value="">All customers</option>
              {options.customers.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label className="fld" style={{ minWidth: 0 }}>
            <span>Enquiry type</span>
            <select value={filters.enquiry_type} onChange={(e) => set({ enquiry_type: e.target.value })}>
              <option value="">All types</option>
              {options.enquiryTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          <label className="fld" style={{ minWidth: 0 }}>
            <span>Purchaser</span>
            <select value={filters.purchaser} onChange={(e) => set({ purchaser: e.target.value })}>
              <option value="">All purchasers</option>
              {options.purchasers.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>

          {/*
            `max`/`min` cross-bound the pair, so an end before the start — a range that can only
            ever match nothing — cannot be entered at all.
          */}
          <label className="fld" style={{ minWidth: 0 }}>
            <span>Dated from</span>
            <input
              type="date"
              value={filters.date_from}
              max={filters.date_to || undefined}
              onChange={(e) => set({ date_from: e.target.value })}
            />
          </label>

          <label className="fld" style={{ minWidth: 0 }}>
            <span>Dated to</span>
            <input
              type="date"
              value={filters.date_to}
              min={filters.date_from || undefined}
              onChange={(e) => set({ date_to: e.target.value })}
            />
          </label>
        </div>
      ) : null}
    </div>
  )
}
